import dayjs from 'dayjs'

// types
import type { CalendarViewType } from '@/types/components/calendar'

/** Base path for the appointments calendar */
export const APPOINTMENTS_CALENDAR_BASE = '/appointments/calendar'

/** Route or query values Vue Router may pass as a string, null, or a repeated list */
export type CalendarRouteParam = string | null | undefined | Array<string | null | undefined>

/**
 * Options used to resolve the visible calendar view and cursor date from the URL.
 */
export interface ResolveAppointmentsCalendarLocationInput {
  /** Path param `:view` */
  view?: CalendarRouteParam
  /** Path param `:year` */
  year?: CalendarRouteParam
  /** Path param `:month` */
  month?: CalendarRouteParam
  /** Path param `:day` */
  day?: CalendarRouteParam
  /** Legacy `?view=` */
  queryView?: CalendarRouteParam
  /** Legacy `?date=` */
  queryDate?: CalendarRouteParam
  /** Last view stored locally when the URL omits one */
  fallbackView?: string | null
  /** Last date stored locally when the URL omits one */
  fallbackDate?: string | null
}

/**
 * Picks the first string from a Vue Router param or query value.
 * @param value - Raw route value
 */
function firstParam(value: CalendarRouteParam): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value
  }

  // Repeated query keys arrive as arrays
  if (Array.isArray(value)) {
    const first = value[0]

    if (typeof first === 'string' && first.length > 0) {
      return first
    }
  }

  return undefined
}

/**
 * Maps persisted or routed view names onto month, week, or day.
 * Unknown values fall back to month.
 * @param value - Stored or routed view id
 */
export function parseCalendarView(value: string | null | undefined): CalendarViewType {
  if (value === 'week' || value === 'timeGridWeek' || value === 'listWeek') {
    return 'week'
  }

  if (value === 'day' || value === 'timeGridDay' || value === 'listDay') {
    return 'day'
  }

  return 'month'
}

/**
 * Normalises a stored date to 'YYYY-MM-DD'.
 * @param value - ISO timestamp or calendar date
 */
export function parseCalendarDate(value: string | null | undefined): string {
  const parsed = dayjs(value)

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
}

/**
 * Builds a shareable calendar path, e.g. `/appointments/calendar/day/2026/8/17`.
 * @param view - Active layout
 * @param date - Cursor date as 'YYYY-MM-DD' or ISO
 */
export function buildAppointmentsCalendarPath(view: CalendarViewType, date: string): string {
  const parsed = dayjs(date)
  const cursor = parsed.isValid() ? parsed : dayjs()

  return `${APPOINTMENTS_CALENDAR_BASE}/${view}/${cursor.year()}/${cursor.month() + 1}/${cursor.date()}`
}

/**
 * Reads view and date from path params, then query, then local fallbacks.
 * Path wins so a shared link is not overridden by the visitor's last session.
 * @param input - Route params, query, and optional stored fallbacks
 */
export function resolveAppointmentsCalendarLocation(
  input: ResolveAppointmentsCalendarLocationInput
): { view: CalendarViewType; date: string } {
  const year = firstParam(input.year)
  const month = firstParam(input.month)
  const day = firstParam(input.day)

  // Google-style `/view/2026/8/17` is complete only when all three parts are present
  const pathDate =
    year && month && day ? parseCalendarDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`) : undefined

  const view = parseCalendarView(firstParam(input.view) ?? firstParam(input.queryView) ?? input.fallbackView)
  const date = pathDate ?? parseCalendarDate(firstParam(input.queryDate) ?? input.fallbackDate)

  return { view, date }
}
